import { cleanStorefrontProductCopy, cleanStorefrontProductList } from './productCopy'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
export const TENANT_SLUG = import.meta.env.VITE_TENANT_SLUG
const THUMBOR_URL = import.meta.env.VITE_THUMBOR_URL
const ENABLE_API_CACHE = import.meta.env.PROD
const API_CACHE_TTL_MS = 60 * 60 * 1000
const API_STALE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000
const PRODUCTS_STALE_CACHE_TTL_MS = 24 * 60 * 60 * 1000
export const STOREFRONT_API_REQUEST_TIMEOUT_MS = 60_000
const API_FETCH_ATTEMPTS = 2
const API_RETRY_DELAY_MS = 150
const API_CIRCUIT_FAILURE_THRESHOLD = 2
const API_CIRCUIT_COOLDOWN_MS = 30 * 1000

if (!API_BASE_URL) {
  throw new Error('Missing VITE_API_BASE_URL environment variable')
}

if (!TENANT_SLUG) {
  throw new Error('Missing VITE_TENANT_SLUG environment variable')
}

if (!THUMBOR_URL) {
  throw new Error('Missing VITE_THUMBOR_URL environment variable')
}

export type StorefrontConfig = {
  store_name: string
  store_description?: string | null
  contact_email?: string | null
  contact_phone?: string | null
  banner_image_urls?: string[]
  video_urls?: string[]
  currency: string
  currency_symbol: string
  currency_symbol_position: 'before' | 'after' | string
  payment_enabled: boolean
  shipping_enabled: boolean
  guest_checkout_enabled: boolean
  minimum_order_amount: string | null
  terms_url: string | null
  privacy_url: string | null
}

export type StorefrontCategory = {
  id: string
  name: string
  slug: string
  description: string | null
  image_url: string | null
  parent_id: string | null
  sort_order: number
  children: StorefrontCategory[]
}

export type StorefrontPhoto = {
  id: string
  url: string
  alt_text: string | null
  caption: string | null
  media_type: string
  is_featured: boolean
  sort_order: number
}

export type StorefrontVariant = {
  id: string
  sku: string
  name: string | null
  price: string
  original_price: string | null
  stock: number
  track_inventory: boolean
  allow_backorder: boolean
  weight: string | null
  weight_unit: string
  is_active: boolean
  sort_order: number
  photos: StorefrontPhoto[]
}

export type StorefrontProduct = {
  id: string
  name: string
  slug: string
  sku: string
  description: string | null
  status: string
  published_at: string | null
  is_digital: boolean
  meta_title: string | null
  meta_description: string | null
  photos: StorefrontPhoto[]
  variants: StorefrontVariant[]
  categories: Array<Pick<StorefrontCategory, 'id' | 'name' | 'slug'>>
  tags: Array<{ id: string; name: string; slug: string }>
}

const GLOVE_CATEGORY = {
  id: 'ac761766-d7fe-4f40-8c84-83fc745bec5f',
  name: 'Manshet & Sarung Tangan',
  slug: 'manshet-sarung-tangan',
} as const

// Products supplied as code-owned catalog assets. They are merged into the
// live storefront response for their category and search results, and remain
// fully addressable through /produk/$slug. If a dashboard product with the
// same product or slug is published later, the dashboard record takes precedence.
export const CURATED_STOREFRONT_PRODUCTS: StorefrontProduct[] = [
  {
    id: '52430002-0000-4000-8000-000000000002',
    name: 'Sarung Tangan Industri CPR5243-2 Merah',
    slug: 'sarung-tangan-industri-cpr5243-2-merah',
    sku: 'CPR5243-2',
    description:
      'Sarung tangan kerja industri warna merah size 10 dengan lapisan hitam pada telapak dan ujung jari. Cocok untuk aktivitas produksi, material handling, gudang, dan pekerjaan umum yang membutuhkan cengkeraman lebih mantap.',
    status: 'published',
    published_at: '2026-08-12T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Sarung Tangan Industri CPR5243-2 Merah Size 10 | Dropify',
    meta_description:
      'Grosir sarung tangan industri CPR5243-2 merah size 10 dengan lapisan hitam pada telapak dan ujung jari. Cocok untuk pabrik, handling, dan gudang.',
    photos: [
      {
        id: '52432002-0000-4000-8000-000000000002',
        url: '/products/sarung-tangan-industri-cpr5243-2.webp',
        alt_text: 'Sarung Tangan Industri CPR5243-2 Merah Size 10',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: '52430000-0000-4000-8000-000000000001', name: 'Sarung Tangan Industri', slug: 'sarung-tangan-industri' }],
  },
  {
    id: '52430003-0000-4000-8000-000000000003',
    name: 'Sarung Tangan Industri CPR5243-3 Biru',
    slug: 'sarung-tangan-industri-cpr5243-3-biru',
    sku: 'CPR5243-3',
    description:
      'Sarung tangan kerja industri warna biru size 10 dengan lapisan hitam pada telapak dan ujung jari. Cocok untuk operasional pabrik, perakitan, material handling, gudang, dan bongkar muat.',
    status: 'published',
    published_at: '2026-08-12T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Sarung Tangan Industri CPR5243-3 Biru Size 10 | Dropify',
    meta_description:
      'Grosir sarung tangan industri CPR5243-3 biru size 10 dengan lapisan hitam pada telapak dan ujung jari. Cocok untuk pabrik, handling, dan gudang.',
    photos: [
      {
        id: '52433003-0000-4000-8000-000000000003',
        url: '/products/sarung-tangan-industri-cpr5243-3.webp',
        alt_text: 'Sarung Tangan Industri CPR5243-3 Biru Size 10',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: '52430000-0000-4000-8000-000000000001', name: 'Sarung Tangan Industri', slug: 'sarung-tangan-industri' }],
  },
  {
    id: '52430004-0000-4000-8000-000000000004',
    name: 'Sarung Tangan Industri CPR5243-4 Hijau',
    slug: 'sarung-tangan-industri-cpr5243-4-hijau',
    sku: 'CPR5243-4',
    description:
      'Sarung tangan kerja industri warna hijau size 10 dengan lapisan hitam pada telapak dan ujung jari. Cocok untuk aktivitas produksi, material handling, gudang, dan pekerjaan lapangan.',
    status: 'published',
    published_at: '2026-08-12T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Sarung Tangan Industri CPR5243-4 Hijau Size 10 | Dropify',
    meta_description:
      'Grosir sarung tangan industri CPR5243-4 hijau size 10 dengan lapisan hitam pada telapak dan ujung jari. Cocok untuk pabrik, handling, dan gudang.',
    photos: [
      {
        id: '52434004-0000-4000-8000-000000000004',
        url: '/products/sarung-tangan-industri-cpr5243-4.webp',
        alt_text: 'Sarung Tangan Industri CPR5243-4 Hijau Size 10',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: '52430000-0000-4000-8000-000000000001', name: 'Sarung Tangan Industri', slug: 'sarung-tangan-industri' }],
  },
  {
    id: '7e6b0001-0000-4000-8000-000000000001',
    name: 'Sarung Tangan Gekko PU Biru',
    slug: 'sarung-tangan-gekko-pu-biru',
    sku: '',
    description:
      'Sarung tangan kerja dengan bahan dasar rajut putih dan lapisan PU biru pada telapak serta jari. Biasa dipilih untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Konfirmasikan ukuran, cetakan standar, dan dokumen seri yang tersedia sebelum PO.',
    status: 'published',
    published_at: '2026-08-16T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Grosir Sarung Tangan Gekko PU Biru | Dropify',
    meta_description:
      'Grosir sarung tangan Gekko PU biru untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Cek stok dan quotation via WhatsApp.',
    photos: [
      {
        id: '7e6b1001-0000-4000-8000-000000000001',
        url: '/lp/gekko-biru.jpeg',
        alt_text: 'Sarung tangan Gekko PU Biru',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: '7e6b9000-0000-4000-8000-000000000001', name: 'Gekko Grip', slug: 'gekko-grip' }],
  },
  {
    id: '7e6b0002-0000-4000-8000-000000000002',
    name: 'Sarung Tangan Gekko PU Hitam',
    slug: 'sarung-tangan-gekko-pu-hitam',
    sku: '',
    description:
      'Sarung tangan kerja dengan bahan dasar polyester hitam dan lapisan PU hitam pada telapak serta jari. Biasa dipilih untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Konfirmasikan ukuran, cetakan standar, dan dokumen seri yang tersedia sebelum PO.',
    status: 'published',
    published_at: '2026-08-16T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Grosir Sarung Tangan Gekko PU Hitam | Dropify',
    meta_description:
      'Grosir sarung tangan Gekko PU hitam untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Cek stok dan quotation via WhatsApp.',
    photos: [
      {
        id: '7e6b1002-0000-4000-8000-000000000002',
        url: '/lp/gekko-hitam.jpeg',
        alt_text: 'Sarung tangan Gekko PU Hitam',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: '7e6b9000-0000-4000-8000-000000000001', name: 'Gekko Grip', slug: 'gekko-grip' }],
  },
  {
    id: '7e6b0003-0000-4000-8000-000000000003',
    name: 'Sarung Tangan Gekko PU Putih-Hitam',
    slug: 'sarung-tangan-gekko-pu-putih-hitam',
    sku: '',
    description:
      'Sarung tangan kerja dengan bahan dasar rajut putih dan lapisan PU hitam pada telapak serta jari. Biasa dipilih untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Konfirmasikan ukuran, cetakan standar, dan dokumen seri yang tersedia sebelum PO.',
    status: 'published',
    published_at: '2026-08-16T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Grosir Sarung Tangan Gekko PU Putih-Hitam | Dropify',
    meta_description:
      'Grosir sarung tangan Gekko PU putih-hitam untuk perakitan, pengepakan, gudang, dan penanganan barang kering. Cek stok dan quotation via WhatsApp.',
    photos: [
      {
        id: '7e6b1003-0000-4000-8000-000000000003',
        url: '/lp/gekko-putih.jpeg',
        alt_text: 'Sarung tangan Gekko PU Putih-Hitam',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: '7e6b9000-0000-4000-8000-000000000001', name: 'Gekko Grip', slug: 'gekko-grip' }],
  },
  {
    id: 'd1a00001-0000-4000-8000-000000000001',
    name: 'Sarung Tangan Diamond',
    slug: 'sarung-tangan-diamond',
    sku: '',
    description:
      'Sarung tangan kerja rajut abu-abu dengan bintik PVC kuning pada telapak dan jari. Ditujukan untuk pekerjaan umum ringan, pengemasan, gudang, dan pemindahan barang kering. Konfirmasikan warna, ukuran, jumlah, dan foto stok aktual sebelum memesan.',
    status: 'published',
    published_at: '2026-08-16T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Grosir Sarung Tangan Diamond | Dropify',
    meta_description:
      'Grosir sarung tangan Diamond rajut abu-abu dengan bintik PVC kuning untuk gudang, packing, dan kerja umum. Cek stok serta harga via WhatsApp.',
    photos: [
      {
        id: 'd1a01001-0000-4000-8000-000000000001',
        url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/06/11/c9c7d0901ffa450fb195938189e99096.jpeg',
        alt_text: 'Sarung tangan Diamond rajut abu-abu dengan bintik PVC kuning',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: 'd1a09000-0000-4000-8000-000000000001', name: 'Diamond', slug: 'diamond' }],
  },
  {
    id: 'b01d0001-0000-4000-8000-000000000001',
    name: 'Sarung Tangan Bulldozer',
    slug: 'sarung-tangan-bulldozer',
    sku: '',
    description:
      '<p>Sarung Tangan Bulldozer berwarna abu-abu, memakai rajutan tanpa bintik, dan memiliki pergelangan elastis.</p><ul><li>Warna: abu-abu.</li><li>Permukaan: rajut tanpa bintik.</li><li>Isi kemasan: 12 pasang.</li></ul><p>Konfirmasikan jumlah dan stok sebelum memesan. Jika produk akan dipakai pada pekerjaan dengan risiko sayatan, panas, bahan kimia, atau listrik, kirimkan standar perlindungan yang diwajibkan agar admin dapat membantu memeriksa pilihan yang sesuai.</p>',
    status: 'published',
    published_at: '2026-08-16T00:00:00+07:00',
    is_digital: false,
    meta_title: 'Sarung Tangan Bulldozer Grosir | Dropify',
    meta_description:
      'Sarung Tangan Bulldozer abu-abu dalam kemasan 12 pasang. Cek stok aktual, jumlah grosir, spesifikasi, dan pengiriman melalui WhatsApp.',
    photos: [
      {
        id: 'b01d1001-0000-4000-8000-000000000001',
        url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/eca092f285324e848ac405f2dca35f17.jpeg',
        alt_text: 'Sarung Tangan Bulldozer abu-abu dan kemasan 12 pasang',
        caption: null,
        media_type: 'image',
        is_featured: true,
        sort_order: 0,
      },
      {
        id: 'b01d1002-0000-4000-8000-000000000002',
        url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/4f3ca9c3c13e41928202ee377cbd6074.jpeg',
        alt_text: 'Sarung Tangan Bulldozer abu-abu tampak depan',
        caption: null,
        media_type: 'image',
        is_featured: false,
        sort_order: 1,
      },
      {
        id: 'b01d1003-0000-4000-8000-000000000003',
        url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/cdd97b20e84842e4aaf76874924b8b86.jpeg',
        alt_text: 'Sarung Tangan Bulldozer dan kemasan produk',
        caption: null,
        media_type: 'image',
        is_featured: false,
        sort_order: 2,
      },
    ],
    variants: [],
    categories: [GLOVE_CATEGORY],
    tags: [{ id: 'b01d9000-0000-4000-8000-000000000001', name: 'Bulldozer', slug: 'bulldozer' }],
  },
]

export type BlogTag = { name: string; slug: string }

// Mirrors the slug format the blog API uses for tags, so a tag name displayed
// on a card resolves to the same `?tag=` filter value on the tag page.
export function slugifyTag(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function blogTagPath(slug: string): string {
  return `/blog/tag/${encodeURIComponent(slug)}`
}

export type StorefrontBlogPost = {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  featured_image_url: string | null
  published_at: string | null
  tags: BlogTag[]
  meta_title?: string | null
  meta_description?: string | null
  author?: string | null
  featured_image_alt?: string | null
}

export type StorefrontTestimonialMedia = {
  id: string
  url: string
  media_type: 'image' | 'video'
  alt_text: string | null
  /** ISO upload date parsed from the media storage path, when available. */
  uploaded_at: string | null
  // Proxy for created/updated time: upload date parsed from the storage URL
  // path (/YYYY/MM/DD/) combined with sort_order. Higher = newer.
  sortKey: number
}

export type StorefrontTestimonial = {
  id: string
  giver_name: string | null
  description: string | null
  rate: string | null
  is_active: boolean
  media: StorefrontTestimonialMedia[]
}

type PaginatedResponse<T> = {
  items: T[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

type CacheEntry<T> = {
  expiresAt: number
  staleUntil: number
  data?: T
  promise?: Promise<T>
}

type CircuitState = {
  failures: number
  openUntil: number
}

export type ImageTransformOptions = {
  width?: number
  height?: number
  fitIn?: boolean
  smart?: boolean
  format?: 'avif' | 'webp' | 'jpeg' | 'png'
  quality?: number
}

export type ImageSrcSetVariant = {
  width: number
  options: ImageTransformOptions
}

const apiCache = new Map<string, CacheEntry<unknown>>()
const apiCircuits = new Map<string, CircuitState>()

// Product data changes more often than config/categories, so it gets a short
// TTL instead of the 1h default. The short window still collapses the
// homepage's per-slug fan-out (and concurrent visitors) into one upstream
// request per product, which matters because every /storefront/products call
// reaches the dashboard API's database.
const PRODUCTS_CACHE_TTL_MS = 60 * 1000
// Editors update published articles in the dashboard, including corrections
// to sensitive images. Keep detail and archive caches on the same short TTL.
const BLOG_CACHE_TTL_MS = 60 * 1000

function getApiCacheTtlMs(path: string) {
  if (path.startsWith('/storefront/products')) return PRODUCTS_CACHE_TTL_MS
  if (path.startsWith('/storefront/blog/posts')) return BLOG_CACHE_TTL_MS
  return API_CACHE_TTL_MS
}

function getApiStaleCacheTtlMs(path: string) {
  if (path.startsWith('/storefront/products')) return PRODUCTS_STALE_CACHE_TTL_MS
  return API_STALE_CACHE_TTL_MS
}

function getApiCircuitKey(path: string, params?: Record<string, string | number | undefined>) {
  if (path.startsWith('/storefront/products')) return 'products'
  if (path.startsWith('/storefront/categories')) return 'categories'
  // A broken category filter must not block the unfiltered news fallback.
  if (path === '/storefront/blog/posts' && params?.category) return 'blog_category_list'
  if (path.startsWith('/storefront/blog')) return 'blog'
  if (path.startsWith('/storefront/testimonials')) return 'testimonials'
  return 'config'
}

function isApiCircuitOpen(key: string) {
  return (apiCircuits.get(key)?.openUntil ?? 0) > Date.now()
}

function recordApiSuccess(key: string) {
  apiCircuits.delete(key)
}

function recordApiFailure(key: string) {
  const current = apiCircuits.get(key) ?? { failures: 0, openUntil: 0 }
  const failures = current.failures + 1
  apiCircuits.set(key, {
    failures,
    openUntil: failures >= API_CIRCUIT_FAILURE_THRESHOLD
      ? Date.now() + API_CIRCUIT_COOLDOWN_MS
      : 0,
  })
}

function withTenant(path: string, params?: Record<string, string | number | undefined>) {
  const url = new URL(`${API_BASE_URL}${path}`)
  url.searchParams.set('tenant_slug', TENANT_SLUG)

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  return url
}

// Carries the HTTP status so callers can distinguish a genuine 404 (deleted
// product/post → should serve a 404 page) from a real upstream failure.
export class DropifyApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message)
    this.name = 'DropifyApiError'
  }
}

function isRetryableApiStatus(status: number) {
  return status === 429 || status >= 500
}

function waitForApiRetry(attempt: number) {
  return new Promise((resolve) => setTimeout(resolve, API_RETRY_DELAY_MS * attempt))
}

async function fetchDropifyJson<T>(url: URL) {
  let lastError: unknown
  const startedAt = Date.now()

  for (let attempt = 1; attempt <= API_FETCH_ATTEMPTS; attempt += 1) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), STOREFRONT_API_REQUEST_TIMEOUT_MS)

    try {
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Tenant-Slug': TENANT_SLUG,
        },
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new DropifyApiError(
          response.status,
          `Dropify API request failed: ${response.status} ${response.statusText}`,
        )
      }

      return await response.json() as T
    } catch (error) {
      lastError = error
      // A second request helps for an immediate 429/5xx response. Retrying a
      // timeout or connection failure only extends SSR latency while the
      // upstream is already unreachable.
      const canRetry = error instanceof DropifyApiError
        && isRetryableApiStatus(error.status)

      if (!canRetry || attempt === API_FETCH_ATTEMPTS) {
        if (import.meta.env.SSR) {
          // Endpoint classes only: no query strings, slug, tenant, or credentials.
          const endpoint = url.pathname.includes('/storefront/products/') ? 'product'
            : url.pathname.includes('/storefront/products') ? 'product_list'
              : url.pathname.includes('/storefront/categories') ? 'categories' : 'optional_storefront'
          console.warn(JSON.stringify({
            event: 'storefront_api_failed', endpoint,
            status: error instanceof DropifyApiError ? error.status : null,
            errorClass: error instanceof DropifyApiError ? 'http'
              : error instanceof Error && error.name === 'AbortError' ? 'timeout' : 'network_or_parse',
            attempts: attempt, elapsedMs: Date.now() - startedAt,
            timestamp: new Date().toISOString(),
            deployment: process.env.DEPLOYMENT_SHA || 'unknown',
          }))
        }
        throw error
      }
    } finally {
      clearTimeout(timeout)
    }

    await waitForApiRetry(attempt)
  }

  throw lastError
}

async function dropifyFetch<T>(path: string, params?: Record<string, string | number | undefined>) {
  const url = withTenant(path, params)
  const cacheKey = url.toString()
  const cacheTtlMs = getApiCacheTtlMs(path)
  const staleCacheTtlMs = getApiStaleCacheTtlMs(path)
  const circuitKey = getApiCircuitKey(path, params)
  const now = Date.now()
  const cached = apiCache.get(cacheKey) as CacheEntry<T> | undefined

  if (ENABLE_API_CACHE && cacheTtlMs > 0) {
    if (cached?.data !== undefined && cached.expiresAt > now) {
      return cached.data
    }

    if (cached?.promise) {
      return cached.promise
    }

    if (isApiCircuitOpen(circuitKey)) {
      if (cached?.data !== undefined && cached.staleUntil > now) return cached.data
      throw new DropifyApiError(503, 'Dropify API temporarily unavailable')
    }
  }

  const request = fetchDropifyJson<T>(url)

  if (!ENABLE_API_CACHE || cacheTtlMs <= 0) {
    return request
  }

  const resilientRequest = request.then(
    (data) => {
      recordApiSuccess(circuitKey)
      apiCache.set(cacheKey, {
        expiresAt: Date.now() + cacheTtlMs,
        staleUntil: Date.now() + staleCacheTtlMs,
        data,
      })
      return data
    },
    (error) => {
      recordApiFailure(circuitKey)
      if (cached?.data !== undefined && cached.staleUntil > Date.now()) {
        apiCache.set(cacheKey, { ...cached, promise: undefined })
        return cached.data
      }
      apiCache.delete(cacheKey)
      throw error
    },
  )

  apiCache.set(cacheKey, {
    expiresAt: cached?.expiresAt ?? 0,
    staleUntil: cached?.staleUntil ?? 0,
    data: cached?.data,
    promise: resilientRequest,
  })
  return resilientRequest
}

export async function getStorefrontConfig(): Promise<StorefrontConfig> {
  return dropifyFetch<StorefrontConfig>('/storefront/config').catch(() => ({
    store_name: 'Dropify',
    currency: 'IDR',
    currency_symbol: 'Rp',
    currency_symbol_position: 'before',
    payment_enabled: false,
    shipping_enabled: false,
    guest_checkout_enabled: true,
    minimum_order_amount: null,
    terms_url: null,
    privacy_url: null,
  }))
}

// Categories temporarily not sold. Hidden from every category listing (nav,
// homepage, katalog, LP pages, sitemap), and their /kategori/<slug> and
// /lp/grosir-<slug> routes return 404. Remove a slug to restore it everywhere.
export const HIDDEN_CATEGORY_SLUGS = new Set(['perlengkapan-bayi', 'baju-anak', 'mukena-premium'])

export async function listStorefrontCategories() {
  const categories = await dropifyFetch<StorefrontCategory[]>('/storefront/categories')
  return categories.filter((category) => !HIDDEN_CATEGORY_SLUGS.has(category.slug))
}

export type StorefrontProductListParams = {
  page?: number
  per_page?: number
  category?: string
  search?: string
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
}

function curatedProductsFor(params?: StorefrontProductListParams) {
  const category = params?.category?.trim().toLowerCase()
  const search = params?.search?.trim().toLowerCase()

  // Keep unfiltered homepage/product rails API-driven. Curated products are
  // intentionally injected when their category or a matching search is used.
  if (!category && !search) return []

  return CURATED_STOREFRONT_PRODUCTS.filter((product) => {
    const inCategory = !category || product.categories.some((item) => item.slug.toLowerCase() === category)
    if (!inCategory) return false
    if (!search) return true

    const haystack = [
      product.name,
      product.slug,
      product.sku,
      product.description ?? '',
      ...product.tags.flatMap((tag) => [tag.name, tag.slug]),
    ].join(' ').toLowerCase()

    return search.split(/\s+/).every((term) => haystack.includes(term))
  })
}

function sortStorefrontProducts(products: StorefrontProduct[], params?: StorefrontProductListParams) {
  if (!params?.sort_by) return products

  const direction = params.sort_dir === 'asc' ? 1 : -1
  return [...products].sort((left, right) => {
    if (params.sort_by === 'name') {
      return left.name.localeCompare(right.name, 'id') * direction
    }

    if (params.sort_by === 'published_at') {
      const leftTime = left.published_at ? new Date(left.published_at).getTime() : 0
      const rightTime = right.published_at ? new Date(right.published_at).getTime() : 0
      return (leftTime - rightTime) * direction
    }

    return 0
  })
}

export async function listStorefrontProducts(params?: StorefrontProductListParams) {
  const curated = curatedProductsFor(params)
  if (curated.length === 0) {
    const response = await dropifyFetch<PaginatedResponse<StorefrontProduct>>('/storefront/products', params)
    return cleanStorefrontProductList(response)
  }

  // Fetch the complete filtered API result before merging so pagination stays
  // stable: no remote products are skipped when the local items lead page one.
  const remoteParams = { ...params, page: 1, per_page: 100 }
  const first = await dropifyFetch<PaginatedResponse<StorefrontProduct>>('/storefront/products', remoteParams)
  const remote = [...first.items]

  for (let page = 2; page <= first.total_pages; page += 1) {
    const response = await dropifyFetch<PaginatedResponse<StorefrontProduct>>('/storefront/products', {
      ...remoteParams,
      page,
    })
    remote.push(...response.items)
  }

  const dashboardIdentities = new Set(remote.flatMap((product) => [product.slug.toLowerCase(), product.sku.toLowerCase()]))
  const localOnly = curated.filter(
    (product) =>
      !dashboardIdentities.has(product.slug.toLowerCase())
      && (!product.sku || !dashboardIdentities.has(product.sku.toLowerCase())),
  )
  const combined = sortStorefrontProducts([...localOnly, ...remote], params)
  const page = params?.page ?? 1
  const perPage = params?.per_page ?? 20
  const start = (page - 1) * perPage

  return {
    items: combined.slice(start, start + perPage).map(cleanStorefrontProductCopy),
    total: combined.length,
    page,
    per_page: perPage,
    total_pages: Math.max(1, Math.ceil(combined.length / perPage)),
  }
}

export async function getStorefrontProduct(idOrSlug: string) {
  const curated = CURATED_STOREFRONT_PRODUCTS.find(
    (product) => product.id === idOrSlug || product.slug.toLowerCase() === idOrSlug.toLowerCase(),
  )
  if (!curated) {
    const product = await dropifyFetch<StorefrontProduct>(`/storefront/products/${encodeURIComponent(idOrSlug)}`)
    return cleanStorefrontProductCopy(product)
  }

  try {
    // Prefer the dashboard record once the same slug has been published there.
    const product = await dropifyFetch<StorefrontProduct>(`/storefront/products/${encodeURIComponent(curated.slug)}`)
    return cleanStorefrontProductCopy(product)
  } catch (error) {
    if (error instanceof DropifyApiError && error.status === 404) return cleanStorefrontProductCopy(curated)
    throw error
  }
}

export async function listStorefrontBlogPosts(params?: {
  page?: number
  per_page?: number
  category?: string
  tag?: string
  search?: string
}) {
  return dropifyFetch<PaginatedResponse<Record<string, unknown>>>('/storefront/blog/posts', params)
}

export async function getStorefrontBlogPost(slug: string) {
  return dropifyFetch<Record<string, unknown>>(`/storefront/blog/posts/${encodeURIComponent(slug)}`)
}

export type StorefrontTestimonialListParams = {
  page?: number
  per_page?: number
}

export async function listStorefrontTestimonials(params?: StorefrontTestimonialListParams) {
  return dropifyFetch<PaginatedResponse<Record<string, unknown>> | Record<string, unknown>[]>('/storefront/testimonials', params)
}

export function getProductPrimaryImage(
  product: Pick<StorefrontProduct, 'photos' | 'variants' | 'name'>,
  options?: ImageTransformOptions,
) {
  const imageUrl = getProductPrimaryImageUrl(product)
  return imageUrl ? toExternalImage(imageUrl, options) : placeholderImage(product.name)
}

export function getProductPrimaryImageUrl(
  product: Pick<StorefrontProduct, 'photos' | 'variants'>,
) {
  const featuredPhoto = product.photos.find((photo) => photo.is_featured)
  const firstPhoto = product.photos[0]
  const variantPhoto = product.variants.flatMap((variant) => variant.photos)[0]
  return featuredPhoto?.url ?? firstPhoto?.url ?? variantPhoto?.url ?? null
}

export function getProductPrimaryImageSrcSet(
  product: Pick<StorefrontProduct, 'photos' | 'variants'>,
  variants: ReadonlyArray<ImageSrcSetVariant>,
) {
  const imageUrl = getProductPrimaryImageUrl(product)
  return imageUrl ? toExternalImageSrcSet(imageUrl, variants) : undefined
}

export function getProductPrice(product: Pick<StorefrontProduct, 'variants'>) {
  return product.variants[0]?.price ?? null
}

export function getProductOriginalPrice(product: Pick<StorefrontProduct, 'variants'>) {
  return product.variants[0]?.original_price ?? null
}

export function getProductCategoryName(product: Pick<StorefrontProduct, 'categories'>) {
  return product.categories[0]?.name ?? 'Produk'
}

export function placeholderImage(label: string, width = 600, height = 700) {
  const text = encodeURIComponent(label.slice(0, 48))
  return toExternalImage(`https://placehold.co/${width}x${height}/e2e8f0/64748b?text=${text}`)
}

// Branded fallback for blog posts published without a featured image. Served
// from /public, so it must not go through Thumbor.
export const BLOG_FALLBACK_IMAGE = '/blog-hero-default.svg'

export function toExternalImage(url: string | null | undefined, options?: ImageTransformOptions) {
  if (!url) return ''
  if (!/^https?:\/\//i.test(url)) return url

  const thumborBase = THUMBOR_URL.replace(/\/+$/, '')
  if (url.startsWith(`${thumborBase}/`)) return url

  const operations: string[] = []

  if (options?.fitIn) {
    operations.push('fit-in')
  }

  if (options?.width || options?.height) {
    operations.push(`${options.width ?? 0}x${options.height ?? 0}`)
  }

  if (options?.smart) {
    operations.push('smart')
  }

  const filters: string[] = []
  if (options?.format) {
    filters.push(`format(${options.format})`)
  }
  if (options?.quality) {
    filters.push(`quality(${options.quality})`)
  }

  if (filters.length > 0) {
    operations.push(`filters:${filters.join(':')}`)
  }

  return operations.length > 0
    ? `${thumborBase}/${operations.join('/')}/${url}`
    : `${thumborBase}/${url}`
}

export function toExternalImageSrcSet(url: string | null | undefined, variants: ReadonlyArray<ImageSrcSetVariant>) {
  if (!url) return undefined

  return variants
    .map((variant) => `${toExternalImage(url, variant.options)} ${variant.width}w`)
    .join(', ')
}

const HTML_IMAGE_TRANSFORM = { format: 'webp', quality: 82 } as const

function rewriteHtmlSrcSet(value: string, options: ImageTransformOptions) {
  return value
    .split(',')
    .map((candidate) => {
      const [url, ...descriptor] = candidate.trim().split(/\s+/)
      if (!url) return candidate

      return [toExternalImage(url, options), ...descriptor].join(' ')
    })
    .join(', ')
}

/**
 * Routes every remote image embedded in CMS HTML through Thumbor. Local assets
 * stay local and an existing Thumbor URL is left unchanged, so the transform is
 * safe to apply more than once while content passes through SSR and hydration.
 */
export function rewriteHtmlImagesThroughThumbor(
  html: string,
  options: ImageTransformOptions = HTML_IMAGE_TRANSFORM,
) {
  if (!html) return html

  return html.replace(/<(?:img|source)\b[^>]*>/gi, (tag) =>
    tag.replace(/(\s)(src|srcset)\s*=\s*(["'])(.*?)\3/gi, (_attribute, whitespace, name, quote, value) => {
      const transformed = name.toLowerCase() === 'srcset'
        ? rewriteHtmlSrcSet(value, options)
        : toExternalImage(value, options)

      return `${whitespace}${name}=${quote}${transformed}${quote}`
    }),
  )
}

export function formatMoney(value: string | null | undefined, config?: Pick<StorefrontConfig, 'currency_symbol' | 'currency_symbol_position'>) {
  if (!value) return null

  const amount = Number(value)
  if (!Number.isFinite(amount)) return value

  const formatted = new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount)

  const symbol = config?.currency_symbol ?? 'Rp'
  return config?.currency_symbol_position === 'after' ? `${formatted} ${symbol}` : `${symbol}${formatted}`
}

export function formatPublishedDate(value: string | null | undefined) {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  }).format(date)
}

export function getStoreContactPhone(config?: StorefrontConfig | null) {
  return config?.contact_phone?.trim() || '+62 857-0401-1028'
}

export function getStoreContactEmail(config?: StorefrontConfig | null) {
  return config?.contact_email?.trim() || 'halo@dropify.id'
}

function stableFallbackId(input: Record<string, unknown>) {
  const source = JSON.stringify(input)
  let hash = 2166136261
  for (let index = 0; index < source.length; index += 1) {
    hash = Math.imul(hash ^ source.charCodeAt(index), 16777619)
  }
  return `fallback-${(hash >>> 0).toString(36)}`
}

export function normalizeBlogPost(input: Record<string, unknown>): StorefrontBlogPost {
  const content = String(input.content ?? input.body ?? '')
  const optionalText = (value: unknown) => typeof value === 'string' && value.trim() ? value.trim() : null

  return {
    id: String(input.id ?? input.slug ?? stableFallbackId(input)),
    slug: String(input.slug ?? input.id ?? ''),
    title: String(input.title ?? 'Artikel'),
    excerpt: String(input.excerpt ?? input.summary ?? ''),
    content: rewriteHtmlImagesThroughThumbor(content),
    featured_image_url: typeof input.featured_image_url === 'string'
      ? input.featured_image_url
      : typeof input.image_url === 'string'
        ? input.image_url
        : null,
    published_at: typeof input.published_at === 'string'
      ? input.published_at
      : typeof input.created_at === 'string'
        ? input.created_at
        : null,
    tags: Array.isArray(input.tags)
      ? input.tags
        .map((tag): BlogTag | null => {
          if (typeof tag === 'string') return { name: tag, slug: slugifyTag(tag) }
          if (tag && typeof tag === 'object' && 'name' in tag) {
            const name = String((tag as { name: unknown }).name)
            const slug = 'slug' in tag && (tag as { slug: unknown }).slug
              ? String((tag as { slug: unknown }).slug)
              : slugifyTag(name)
            return name ? { name, slug } : null
          }
          return null
        })
        .filter((tag): tag is BlogTag => tag !== null)
      : [],
    meta_title: optionalText(input.meta_title),
    meta_description: optionalText(input.meta_description),
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function inferMediaType(url: string, fallback?: unknown): 'image' | 'video' | null {
  if (typeof fallback === 'string') {
    const lowered = fallback.toLowerCase()
    if (lowered.includes('video')) return 'video'
    if (lowered.includes('image')) return 'image'
  }

  const normalizedUrl = url.split('?')[0].toLowerCase()
  if (/\.(mp4|webm|mov|m4v|ogg)$/i.test(normalizedUrl)) return 'video'
  if (/\.(jpg|jpeg|png|webp|gif|avif|svg)$/i.test(normalizedUrl)) return 'image'
  return null
}

function parseTestimonialMediaPayload(input: unknown): unknown[] {
  if (Array.isArray(input)) return input
  if (typeof input !== 'string' || !input.trim()) return []

  try {
    const parsed = JSON.parse(input)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Extracts and validates the ISO upload date encoded in a media storage URL. */
export function getMediaUploadDate(value: string): string | null {
  const match = value.match(/(?:^|\/)(\d{4})[/-](\d{2})[/-](\d{2})(?:\/|$)/)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return null

  return `${match[1]}-${match[2]}-${match[3]}`
}

/** Returns the newest upload date found in cached/fetched media URLs. */
export function getLatestMediaUploadDate(values: Iterable<string>): string | null {
  let latest: string | null = null
  for (const value of values) {
    const date = getMediaUploadDate(value)
    if (date && (!latest || date > latest)) latest = date
  }
  return latest
}

/**
 * Shows newly uploaded media first while preserving the configured order for
 * items uploaded on the same day. URLs without an encoded date stay last.
 */
export function sortMediaUrlsNewestFirst(values: Iterable<string>): string[] {
  return Array.from(values, (url, index) => ({
    url,
    index,
    uploadDate: getMediaUploadDate(url),
  }))
    .sort((left, right) => {
      if (left.uploadDate !== right.uploadDate) {
        if (!left.uploadDate) return 1
        if (!right.uploadDate) return -1
        return right.uploadDate.localeCompare(left.uploadDate)
      }

      return left.index - right.index
    })
    .map(({ url }) => url)
}

// Derives a chronological sort key for a testimonial media item. The upload
// date encoded in the storage URL path (/YYYY/MM/DD/) is the primary signal,
// with sort_order as a same-day tiebreaker. Larger key = more recent.
function testimonialMediaSortKey(url: string, sortOrder: unknown): number {
  const uploadDate = getMediaUploadDate(url)
  const dateValue = uploadDate ? Number(uploadDate.replaceAll('-', '')) : 0
  const order = typeof sortOrder === 'number' && Number.isFinite(sortOrder) ? sortOrder : 0
  return dateValue * 100000 + order
}

const TESTIMONIAL_MEDIA_REPLACEMENTS: Readonly<Record<string, string>> = {
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/07/efc6104eebd745a7b71907c16113ddae.jpg':
    'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/19/b8e2501f5075445991bf10f3f07c96a2.jpg',
}

function replaceTestimonialMediaUrl(url: string): string {
  return TESTIMONIAL_MEDIA_REPLACEMENTS[url] ?? url
}

function normalizeTestimonialMediaItem(input: unknown, fallbackId: string): StorefrontTestimonialMedia | null {
  if (typeof input === 'string') {
    const url = replaceTestimonialMediaUrl(input)
    const mediaType = inferMediaType(url)
    if (!mediaType) return null
    return {
      id: fallbackId,
      url,
      media_type: mediaType,
      alt_text: null,
      uploaded_at: getMediaUploadDate(url),
      sortKey: testimonialMediaSortKey(url, undefined),
    }
  }

  const item = asRecord(input)
  if (!item) return null

  const sourceUrl = [item.url, item.storage_url, item.file_url, item.media_url, item.preview_url]
    .find((value): value is string => typeof value === 'string' && value.length > 0)

  if (!sourceUrl) return null

  const url = replaceTestimonialMediaUrl(sourceUrl)

  const mediaType = inferMediaType(
    url,
    item.media_type ?? item.type ?? item.file_type ?? item.mime_type,
  )

  if (!mediaType) return null

  // Prefer the date baked into the storage URL (upload date) over the display
  // URL, which may be a CDN/transform host without the dated path.
  const dateUrl = replaceTestimonialMediaUrl(
    [item.storage_url, item.url, item.file_url, item.media_url, item.preview_url]
      .find((value): value is string => typeof value === 'string' && value.length > 0) ?? url,
  )

  return {
    id: String(item.id ?? fallbackId),
    url,
    media_type: mediaType,
    alt_text: typeof item.alt_text === 'string'
      ? item.alt_text
      : typeof item.caption === 'string'
        ? item.caption
        : null,
    uploaded_at: getMediaUploadDate(dateUrl),
    sortKey: testimonialMediaSortKey(dateUrl, item.sort_order),
  }
}

export function normalizeStorefrontTestimonial(input: Record<string, unknown>): StorefrontTestimonial {
  const testimonialId = String(input.id ?? input.slug ?? stableFallbackId(input))
  const mediaSources = [
    ...parseTestimonialMediaPayload(input.media_payload),
    ...(Array.isArray(input.media) ? input.media : []),
    ...(Array.isArray(input.media_items) ? input.media_items : []),
    ...(Array.isArray(input.files) ? input.files : []),
  ]

  if (typeof input.image_url === 'string') {
    mediaSources.push({ url: input.image_url, media_type: 'image' })
  }

  if (typeof input.video_url === 'string') {
    mediaSources.push({ url: input.video_url, media_type: 'video' })
  }

  return {
    id: testimonialId,
    giver_name: typeof input.giver_name === 'string' ? input.giver_name : null,
    description: typeof input.description === 'string' ? input.description : null,
    rate: typeof input.rate === 'string' ? input.rate : null,
    is_active: input.is_active !== false && input.is_active !== 'false' && input.is_active !== 0,
    media: mediaSources
      .map((item, index) => normalizeTestimonialMediaItem(item, `${testimonialId}-${index}`))
      .filter((item): item is StorefrontTestimonialMedia => Boolean(item)),
  }
}
